import Section from 'app/frontend/base/components/Section'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useMemo, useState } from 'react'
import { OverviewTile, useDefaultOverviewTiles } from './defaults'
import { Button } from '@/components/ui/button'
import { Settings } from 'lucide-react'
import { hasAccessToContent } from 'app/frontend/base/components/ProtectedContent'

import { z } from 'zod'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import { overviewTileTypes } from 'app/frontend/types/custom/OverviewTileType'
import { accessLevels } from 'app/frontend/types/custom/AccessLevel'
import Overview from './components/Overview'
import EditableOverview from './components/EditableOverview'
import { overviewTilePositions } from 'app/frontend/types/custom/OverviewTilePosition'

export const MAIN_FIELD = 'main_overview_tiles_attributes'
export const SIDEBAR_FIELD = 'sidebar_overview_tiles_attributes'

const tileSchema = z.object({
  id: z.number().optional(),
  type: z.enum(overviewTileTypes),
  accessLevel: z.enum(accessLevels),
  order: z.number(),
  config: z.record(z.any()).default({}),
  position: z.enum(overviewTilePositions),
  pending: z.boolean().optional(),
  _destroy: z.boolean().optional(),
})

const formSchema = z.object({
  [MAIN_FIELD]: z.array(tileSchema),
  [SIDEBAR_FIELD]: z.array(tileSchema),
})

export type FormValues = z.infer<typeof formSchema>

const TeamOverview = () => {
  const { team, guild } = useTeam()
  const defaultTiles = useDefaultOverviewTiles()
  const [isCustomizing, setIsCustomizing] = useState(false)

  const hasStoredTiles =
    team.mainOverviewTiles.concat(team.sidebarOverviewTiles).length > 0

  const initialMainTiles = useMemo<OverviewTile[]>(() => {
    return hasStoredTiles
      ? team.mainOverviewTiles
      : defaultTiles.filter((t) => t.position === 'main')
  }, [team])

  const initialSidebarTiles = useMemo<OverviewTile[]>(() => {
    return hasStoredTiles
      ? team.sidebarOverviewTiles
      : defaultTiles.filter((t) => t.position === 'sidebar')
  }, [team])

  const { hasAccess: isAdmin } = hasAccessToContent(
    'admin',
    'base',
    guild,
    team,
  )

  if (!isCustomizing) {
    return (
      <Section title={`${team.name} | ${guild.name}`}>
        {isAdmin && (
          <h2 className='flex items-center justify-end'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setIsCustomizing(true)}
            >
              <Settings className='h-4 w-4 mr-2' />
              Customize
            </Button>
          </h2>
        )}

        <Overview
          mainTiles={initialMainTiles}
          sidebarTiles={initialSidebarTiles}
          isCustomizing={false}
        />
      </Section>
    )
  }

  return (
    <Section title={`${team.name} | ${guild.name}`}>
      <FormWrapper<wowaudit.Team & FormValues>
        formSchema={formSchema}
        entityType='team'
        entity={{
          ...team,
          [MAIN_FIELD]: initialMainTiles,
          [SIDEBAR_FIELD]: initialSidebarTiles,
        }}
        onSave={() => setIsCustomizing(false)}
        transformValues={(values) => ({
          ...values,
          [MAIN_FIELD]: values[MAIN_FIELD].map(
            (tile: OverviewTile, index: number) => ({
              ...tile,
              order: index,
            }),
          ),
          [SIDEBAR_FIELD]: values[SIDEBAR_FIELD].map(
            (tile: OverviewTile, index: number) => ({
              ...tile,
              order: index,
            }),
          ),
        })}
        submitButtons={() => null}
      >
        <EditableOverview onCancel={() => setIsCustomizing(false)} />
      </FormWrapper>
    </Section>
  )
}

export default TeamOverview
